import { useEffect, useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import { Heart, MessageCircle, Eye, Send, Upload as UploadIcon, X, MapPin, Users, User } from "lucide-react";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { sfx } from "@/lib/sounds";
import FollowButton from "@/components/FollowButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["All", "Spawn", "Medieval", "Mega Base", "Arena", "Cyber", "Village", "Redstone", "Memory", "Other"];

function BuildTile({ b, onOpen, onLike }) {
  const navigate = useNavigate();
  const src = b.images?.[0] || b.image;
  const goAuthor = (e) => {
    e.stopPropagation();
    if (b.author_id) { sfx.click(); navigate(`/users/${b.author_id}`); }
  };
  return (
    <div data-testid={`build-tile-${b.id}`} className="relative rounded-xl overflow-hidden group border border-white/10 cursor-pointer" onClick={() => { sfx.click(); onOpen(b.id); }}>
      {src && <img src={src} alt={b.title} className="w-full block group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />

      {/* Author chip in top-right — clickable to open profile */}
      {b.author_id && (
        <button
          onClick={goAuthor}
          data-testid={`build-author-chip-${b.id}`}
          className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 border border-white/10 hover:border-[#FFBF00]/60 text-[10px] uppercase tracking-widest backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
          title={`Open ${b.author_name || b.author}'s profile`}
        >
          {b.author_avatar
            ? <img src={b.author_avatar} alt="" className="w-4 h-4 rounded" />
            : <User size={10} />}
          <span className="max-w-[90px] truncate">{b.author_name || b.author}</span>
        </button>
      )}

      <div className="absolute bottom-3 left-3 right-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]/90">{b.category}</div>
        <div className="font-hero text-xl leading-none mt-1">{b.title}</div>
        <button onClick={goAuthor} className="text-[11px] text-white/60 mt-1 hover:text-[#FFBF00] transition-colors">
          by {b.author_name || b.author}
        </button>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-white/70">
          <button
            onClick={(e) => { e.stopPropagation(); onLike(b.id); }}
            data-testid={`build-like-${b.id}`}
            className={`flex items-center gap-1 hover:text-[#FFBF00] ${b.liked ? "text-[#FFBF00]" : ""}`}
          >
            <Heart size={12} fill={b.liked ? "currentColor" : "none"} /> {b.likes_count ?? 0}
          </button>
          <span className="flex items-center gap-1"><MessageCircle size={12} /> {b.comments_count ?? 0}</span>
          <span className="flex items-center gap-1"><Eye size={12} /> {b.views ?? 0}</span>
        </div>
      </div>
    </div>
  );
}

function BuildModal({ id, onClose, onRefresh }) {
  const { user } = useAuth();
  const [build, setBuild] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (!id) return;
    authClient.get(`/builds/${id}`).then((r) => setBuild(r.data));
    authClient.get(`/builds/${id}/comments`).then((r) => setComments(r.data));
  }, [id]);

  const like = async () => {
    if (!user) return toast.error("Sign in to like builds");
    const { data } = await authClient.post(`/builds/${id}/like`);
    sfx.pling();
    setBuild({ ...build, liked: data.liked, likes_count: data.count });
    onRefresh?.();
  };

  const sendComment = async () => {
    if (!user) return toast.error("Sign in to comment");
    if (!comment.trim()) return;
    try {
      const { data } = await authClient.post(`/builds/${id}/comments`, { body: comment });
      setComments([...comments, data]);
      setComment("");
      sfx.success();
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  if (!build) return null;
  const src = build.images?.[imgIdx] || build.images?.[0] || build.image;

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-black/95 border border-white/10 text-white max-w-4xl p-0 overflow-hidden" data-testid="build-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>{build.title}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2">
          <div className="relative bg-black">
            {src && <img src={src} alt="" className="w-full max-h-[70vh] object-contain" />}
            {build.images?.length > 1 && (
              <div className="flex gap-1 p-2 justify-center overflow-x-auto">
                {build.images.map((im, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`w-14 h-10 rounded overflow-hidden border ${i === imgIdx ? "border-[#FFBF00]" : "border-white/10"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 overflow-y-auto max-h-[80vh]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">{build.category}</div>
            <div className="font-hero text-3xl mt-1">{build.title}</div>
            <div className="flex items-center gap-2 mt-2">
              {build.author_avatar && <img src={build.author_avatar} alt="" className="w-6 h-6 rounded-md" />}
              <span className="text-sm text-white/70">by {build.author_name || build.author}</span>
            </div>
            <p className="text-sm text-white/70 mt-3">{build.description}</p>
            <div className="mt-3 space-y-1 text-[11px] text-white/60 font-mono">
              {build.server && <div>SERVER · {build.server}</div>}
              {build.coordinates && <div className="flex items-center gap-1"><MapPin size={11} /> {build.coordinates}</div>}
              {build.team?.length > 0 && <div className="flex items-center gap-1"><Users size={11} /> {build.team.join(", ")}</div>}
            </div>
            {build.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {build.tags.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-white/70">#{t}</span>)}
              </div>
            )}
            <div className="flex items-center gap-4 mt-4 text-sm">
              <button data-testid="modal-like" onClick={like} className={`flex items-center gap-1.5 ${build.liked ? "text-[#FFBF00]" : "text-white/70 hover:text-[#FFBF00]"}`}>
                <Heart size={16} fill={build.liked ? "currentColor" : "none"} /> {build.likes_count}
              </button>
              <span className="flex items-center gap-1.5 text-white/70"><Eye size={16} /> {build.views}</span>
            </div>

            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Comments · {comments.length}</div>
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1" data-testid="build-comments">
                {comments.map((c) => (
                  <div key={c.id} className="flex items-start gap-2">
                    <img src={c.author_avatar} alt="" className="w-7 h-7 rounded-md" />
                    <div className="flex-1 rounded-lg bg-white/5 p-2">
                      <div className="text-xs font-semibold">{c.author_name}</div>
                      <div className="text-sm text-white/80">{c.body}</div>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && <div className="text-xs text-white/40">Be the first to comment</div>}
              </div>
              {user ? (
                <div className="flex gap-2 mt-3">
                  <input
                    data-testid="build-comment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Say something nice..."
                    className="flex-1 bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FFBF00]"
                    onKeyDown={(e) => e.key === "Enter" && sendComment()}
                  />
                  <button data-testid="build-comment-send" onClick={sendComment} className="px-3 py-2 rounded-md bg-[#FFBF00] hover:bg-[#FF8C00] text-black">
                    <Send size={14} />
                  </button>
                </div>
              ) : (
                <div className="text-xs text-white/50 mt-3">Sign in to comment.</div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Builds() {
  const { user } = useAuth();
  const [builds, setBuilds] = useState([]);
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);

  const load = () => authClient.get("/builds").then((r) => setBuilds(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleLike = async (id) => {
    if (!user) return toast.error("Sign in to like builds");
    try {
      const { data } = await authClient.post(`/builds/${id}/like`);
      sfx.pling();
      setBuilds(builds.map(b => b.id === id ? { ...b, liked: data.liked, likes_count: data.count } : b));
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  const filtered = filter === "All" ? builds : builds.filter(b => b.category === filter);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-builds">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <SectionHeader eyebrow="Gallery" title="Community Builds" subtitle="Curated masterpieces built by the LostDC community over 8 seasons." />
          <Link
            to="/builds/upload"
            data-testid="builds-upload-cta"
            onClick={() => sfx.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold"
          >
            <UploadIcon size={14} /> Upload Build
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="builds-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { setFilter(c); sfx.click(); }}
              data-testid={`builds-filter-${c.toLowerCase().replace(/\s/g,'-')}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
                filter === c ? "bg-[#FFBF00] text-black" : "glass hover:border-[#FFBF00]/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="masonry">
          {filtered.map((b) => (
            <BuildTile key={b.id} b={b} onOpen={setOpenId} onLike={toggleLike} />
          ))}
        </div>
        {filtered.length === 0 && <div className="text-center text-white/50 py-20">No builds in this category yet.</div>}
      </div>

      <BuildModal id={openId} onClose={() => setOpenId(null)} onRefresh={load} />
    </div>
  );
}
